import { ChangeEvent, useState, useCallback, UIEvent, ReactNode, useMemo } from 'react';
import { groupBy } from 'lodash-es';
import {
  Button,
  Checkbox,
  cn,
  Listbox,
  ListboxItem,
  ListboxSection,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollShadow,
  SearchInput,
  Selection,
  Spinner,
  PopoverProps,
} from '@ds';
import { MdOutlineClear, MdOutlineKeyboardArrowDown, MdOutlineFilterAlt } from 'react-icons/md';
import { NameByValue } from '@/types';

export enum DropdownTypeEnum {
  Flat = 'flat',
  Sections = 'sections',
}

type SectionWithItems<T extends NameByValue> = {
  section: string;
  items: T[];
};

type FilterDropdownProps<T extends NameByValue & { section?: string }> = {
  title: string;
  placement?: PopoverProps['placement'];
  isFaded?: boolean;
  items?: T[];
  selectedKeys?: T['value'][];
  disabledKeys?: T['value'][];
  type?: DropdownTypeEnum;
  mode?: 'single' | 'multiple';
  hasNextPage?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  showCounter?: boolean;
  triggerDisabled?: boolean;
  disallowEmptySelection?: boolean;
  size?: 'sm' | 'md';
  onSelectedKeysChange?: (selectedKeys: T['value'][]) => void;
  onSearchValue?: (value: string) => void;
  loadNextPage?: () => void;
  customContent?: ReactNode;
  renderCustomTrigger?: (isOpen: boolean) => ReactNode;
  hasCustomValue?: boolean;
  customValueCount?: number;
  onReset?: () => void;
  showResetButton?: boolean;
  listboxClassName?: string;
  renderListBoxItem?: (item: T) => ReactNode;
  renderListBoxItemEndContent?: (item: T) => ReactNode;
  isDynamicItems?: boolean;
  showIconOnly?: boolean;
};

export const FilterDropdown = <T extends NameByValue>({
  title = '',
  isFaded,
  placement = 'bottom-start',
  items = [],
  selectedKeys = [],
  disabledKeys = [],
  type = DropdownTypeEnum.Flat,
  mode = 'multiple',
  hasNextPage = false,
  isLoading = false,
  showCounter = true,
  triggerDisabled,
  size = 'md',
  onSelectedKeysChange,
  onSearchValue,
  loadNextPage,
  disallowEmptySelection = false,
  customContent,
  renderCustomTrigger,
  hasCustomValue = false,
  customValueCount = 0,
  onReset,
  showResetButton = false,
  listboxClassName,
  renderListBoxItem,
  isDynamicItems = false,
  showIconOnly = false,
  isDisabled,
  renderListBoxItemEndContent,
}: FilterDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMultiselect = mode === 'multiple';
  const hasItems = items.length > 0;
  const isCustomFilter = !!customContent;
  const isCustomTrigger = !!renderCustomTrigger;
  const hasSearchValue = !!onSearchValue;

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSearchValue?.(event.target.value);
  };

  const onSelection = (keys: Selection) => {
    if (!onSelectedKeysChange) return;

    if (keys === 'all') {
      onSelectedKeysChange(items.map((i) => i.value));
      return;
    }

    onSelectedKeysChange(Array.from(keys as Set<T['value']>));
  };

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (!open) {
      onSearchValue?.('');
    }
  };

  const hasScrollHandler = !!loadNextPage && hasNextPage;

  const onScroll = useCallback(
    ({ currentTarget }: UIEvent<HTMLDivElement>) => {
      if (!hasScrollHandler || isLoading) return;

      const isScrolledToBottom =
        currentTarget.scrollHeight - currentTarget.scrollTop <= currentTarget.clientHeight + 30;

      if (isScrolledToBottom) {
        loadNextPage();
      }
    },
    [hasScrollHandler, loadNextPage, isLoading],
  );

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else if (onSelectedKeysChange) {
      onSelectedKeysChange([]);
    }
    onSearchValue?.('');
  };

  const shouldShowCounter =
    showCounter && ((!!selectedKeys.length && selectedKeys[0] !== '') || (isCustomFilter && hasCustomValue));

  const counterValue = isCustomFilter && hasCustomValue ? customValueCount : selectedKeys.length;

  const selectedKeysSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const disabledKeysSet = useMemo(() => new Set(disabledKeys), [disabledKeys]);

  const itemsWithSections = useMemo<SectionWithItems<T>[]>(() => {
    if (type === DropdownTypeEnum.Flat) return [];

    const groupedList = groupBy(items, 'section');
    const sectionsList = Object.keys(groupedList);

    return sectionsList.map((section) => ({
      section,
      items: groupedList[section],
    }));
  }, [items, type]);

  const renderItem = (item: T) => {
    const itemLabel = hasSearchValue ? <span>{item.name}</span> : item.name;

    return (
      <ListboxItem
        startContent={
          isMultiselect ? (
            <div className="h-full flex items-center">
              <Checkbox
                size="sm"
                isReadOnly
                isSelected={selectedKeysSet.has(item.value)}
                hasWrapper
                wrapperClassName="h-4"
                classNames={{ base: 'p-0 m-0', wrapper: 'me-0' }}
              />
            </div>
          ) : null
        }
        key={item.value}
        hideSelectedIcon={isMultiselect}
        classNames={{
          base: cn('h-9 rounded-md gap-0', {
            'h-auto py-2': hasSearchValue,
          }),
          title: cn('text-m-r!', {
            'line-clamp-3': hasSearchValue,
            'ml-2': isMultiselect,
          }),
        }}
        endContent={renderListBoxItemEndContent?.(item)}
        textValue={item.name}
      >
        {renderListBoxItem ? renderListBoxItem(item) : itemLabel}
      </ListboxItem>
    );
  };

  const renderItemsList = () => {
    if (type === DropdownTypeEnum.Sections && itemsWithSections.length > 1) {
      return itemsWithSections.map((item, index) => (
        <ListboxSection key={item.section} showDivider={index !== itemsWithSections.length - 1} title={item.section}>
          {item.items.map(renderItem)}
        </ListboxSection>
      ));
    }

    return items.map(renderItem);
  };

  const dropdownTrigger = useMemo(() => {
    if (isCustomTrigger) {
      return <div className="w-fit">{renderCustomTrigger(isOpen)}</div>;
    }

    if (showIconOnly) {
      return (
        <div
          className={`w-fit ${size === 'sm' ? 'h-8' : 'h-10'} cursor-pointer flex items-center gap-1.5 shrink-0 px-2 rounded-2xl bg-base-50 hover:bg-base-45 aria-expanded:bg-base-45`}
        >
          <MdOutlineFilterAlt size={size === 'sm' ? 16 : 18} className="text-base-25" />
          {shouldShowCounter && (
            <div className="py-0.5 px-2 bg-white rounded-2xl text-center text-s-m min-w-5">{counterValue}</div>
          )}
        </div>
      );
    }

    return (
      <div
        className={`w-fit ${size === 'sm' ? 'h-6' : 'h-8'} cursor-pointer flex items-center gap-2 shrink-0 px-2 rounded-lg hover:bg-base-55 aria-expanded:bg-base-55`}
      >
        <div className="flex items-center gap-1">
          <span
            className={`${size === 'sm' ? 'text-m-m' : 'text-l-m'} ${
              isFaded && !selectedKeys.length && !hasCustomValue ? 'text-base-25' : ''
            }`}
          >
            {title}
          </span>
          {shouldShowCounter && (
            <div className="py-0.5 px-2.5 bg-white rounded-2xl text-center text-s-m">{counterValue}</div>
          )}
        </div>
        <div
          className={`bg-base-10 text-base-90 rounded-full w-4 h-4 flex justify-center items-center ${isOpen ? 'rotate-180' : ''}`}
        >
          <MdOutlineKeyboardArrowDown size={16} />
        </div>
      </div>
    );
  }, [
    counterValue,
    hasCustomValue,
    isCustomTrigger,
    isFaded,
    isOpen,
    renderCustomTrigger,
    selectedKeys.length,
    shouldShowCounter,
    showIconOnly,
    size,
    title,
  ]);

  return (
    <Popover
      isTriggerDisabled={triggerDisabled}
      placement={placement}
      size="md"
      triggerType="listbox"
      triggerScaleOnOpen={false}
      offset={8}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      classNames={{
        content: 'min-w-40 bg-base-60 p-2 rounded-md items-stretch',
        trigger: isDisabled ? 'pointer-events-none opacity-50' : 'opacity-100',
      }}
    >
      <PopoverTrigger>{dropdownTrigger}</PopoverTrigger>
      <PopoverContent
        className={cn({
          'w-72': hasSearchValue,
        })}
      >
        {isCustomFilter && (
          <>
            {customContent}
            {showResetButton && (hasCustomValue || (hasItems && selectedKeys.length > 0)) && (
              <div className="mt-2 flex justify-start">
                <Button
                  onPress={handleReset}
                  size="sm"
                  variant="outline"
                  endContent={<MdOutlineClear size={18} />}
                  className="gap-1 px-3"
                >
                  Reset
                </Button>
              </div>
            )}
          </>
        )}
        {hasSearchValue && (
          <div className="mb-2">
            <SearchInput inputVariant="secondary" placeholder="Search" onChange={onSearchChange} radius="full" />
          </div>
        )}
        {hasItems && (
          <>
            <div className="scroll mt-1">
              <ScrollShadow
                size={10}
                className={cn('w-full max-h-56 overflow-auto', listboxClassName)}
                onScroll={hasScrollHandler ? onScroll : undefined}
              >
                <Listbox
                  aria-label={title}
                  selectionMode={mode}
                  selectedKeys={selectedKeysSet}
                  disabledKeys={disabledKeysSet}
                  hideEmptyContent
                  onSelectionChange={onSelection}
                  disallowEmptySelection={disallowEmptySelection}
                  classNames={{
                    base: 'p-0',
                  }}
                  items={isDynamicItems ? items : undefined}
                >
                  {isDynamicItems ? (item) => renderItem(item) : renderItemsList()}
                </Listbox>
                {isLoading && (
                  <div role="status" className="h-9 w-full flex justify-center items-center">
                    <Spinner size="sm" />
                  </div>
                )}
              </ScrollShadow>
            </div>
            {showResetButton && !isCustomFilter && selectedKeys.length > 0 && (
              <div className="px-4 mt-2 flex justify-start">
                <Button
                  onPress={handleReset}
                  size="sm"
                  variant="outline"
                  endContent={<MdOutlineClear size={18} />}
                  className="gap-1 px-3"
                >
                  Reset
                </Button>
              </div>
            )}
          </>
        )}
        {!hasItems && !isCustomFilter && isLoading && (
          <div role="status" className="h-9 w-full flex justify-center items-center">
            <Spinner size="sm" />
          </div>
        )}
        {!hasItems && !isCustomFilter && !isLoading && (
          <p className="py-3 text-s-r text-base-35 text-center">No results found</p>
        )}
      </PopoverContent>
    </Popover>
  );
};
